import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Sidebar } from 'primereact/sidebar';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import { VerticalFieldAndValueWithIcon } from '@components/ringil3/Containers/VerticalFieldAndValueWithIcon.tsx';
import { HorizontalLine } from '@components/ringil3/styles.tsx';
import { EntityTypeEnum } from '@api/ringil-api.v2.tsx';
import clsx from 'clsx';
import CloseButton from '@components/RingilComponents/CloseButton.tsx';
import useScrollBehindPanel from '@hooks/useScrollBehindPanel/useScrollBehindPanel.tsx';
import { PanelSize, panelSizeClass } from '@components/ringil3/Sidebar/RingilSidebar';
import { sidebarTransitionOptions } from '@components/ringil3/Sidebar/sidebarTransition.ts';

interface ComponentProps {
  /** 'sm' 550px, 'md' 880px, 'lg' 1100px. */
  size?: PanelSize;
  visible: boolean;
  heading: string | JSX.Element;
  onContinue: () => void;
  onBack: () => void;
  loading?: boolean;
  continueLabel?: string | JSX.Element;
  ready?: boolean;
  showSaveButton?: boolean;
  applicationId: string;
  entityType: EntityTypeEnum;
}

const RingilForEntitySidebar: React.FC<PropsWithChildren<ComponentProps>> = ({
  size = 'sm',
  visible,
  heading,
  onContinue,
  onBack,
  children,
  loading = false,
  continueLabel,
  ready = true,
  showSaveButton = true,
  applicationId,
  entityType,
}) => {
  const { tr } = useTranslationLgs();
  useScrollBehindPanel(visible);
  return (
    <>
      {/*
        // @ts-ignore*/}
      <Sidebar
        transitionOptions={sidebarTransitionOptions}
        onHide={onBack}
        visible={visible}
        style={{ backgroundColor: 'var(--ringil3-base-color)' }}
        className={clsx('sidebar-modal', panelSizeClass(size))}
        position={'right'}
        // PrimeReact's own close button is a grey pi-times circle; ours is the same cross the
        // dialogs use, rendered in the header's icons slot instead.
        showCloseIcon={false}
        icons={() => (
          <>
            <SidebarHeader>{heading}</SidebarHeader>
            {/* The sidebar header pads 1rem, not the dialog's 24px, so the cross is not pulled out. */}
            <CloseButton onClick={onBack} className={'mr-0'} />
          </>
        )}
        closeOnEscape={true}
        dismissable={false}
        // The mask still blocks clicks; useScrollBehindPanel forwards the wheel to the content.
        blockScroll={false}
      >
        <div className={'flex flex-col gap-4'}>
          <VerticalFieldAndValueWithIcon
            label={tr(`RingilForEntitySidebar.title${entityType}`, `RingilForEntitySidebar.title${entityType}`)}
            valueText={applicationId}
            iconPath={'/images/icons/ringil3/forExpedition.svg'}
          />
          <HorizontalLine variant={'tertiary'} />
          {children}
        </div>
        <SidebarButtons>
          {showSaveButton && (
            <RingilButton
              label={continueLabel ? continueLabel : tr(`RingilSidebar.continue`, `Continue`)}
              onClick={() => onContinue()}
              mode={'filled'}
              size={'big'}
              loading={loading}
            />
          )}

          <RingilButton label={tr(`RingilSidebar.back`, `Back`)} onClick={() => onBack()} mode={'text-only'} size={'big'} />
        </SidebarButtons>
      </Sidebar>
    </>
  );
};

const SidebarButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const SidebarHeader = styled.div`
  display: flex;
  flex: 1;
  font-family: 'DM Sans';
  font-weight: 400;
  font-size: 24px;
  padding: 0.5rem 0;
`;
export default RingilForEntitySidebar;
